// 💾 src/components/trade/TradeForm.tsx
import React, { useEffect, useState } from "react"
import { auth, db } from "../../services/firebase"
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  increment,
  serverTimestamp
} from "firebase/firestore"
import "./style/TradeForm.scss"
import { toast } from "react-toastify"

interface Trade {
  crypto: string
  tradeAction: "BUY" | "SELL"
  tradePrice: number
  priceNow: number
  tradeAmount: number
  profitLose: number
  createdAt: any
  status: "OPEN" | "CANCELED"
  side: "buy" | "sell"
  orderType: "limit" | "market"
  tp: boolean
  sl: boolean
}

type Props = {
  pair: string
  onNewOrder?: (order: Trade & { id: string }) => void
}

const LOT_SIZES = [0.01, 0.05, 0.1, 0.25, 0.5, 1.0]
const UNITS_PER_LOT = 1000

const TradeForm: React.FC<Props> = ({ pair, onNewOrder }) => {
  const [side, setSide] = useState<"buy" | "sell">("buy")
  const [orderType, setOrderType] = useState<"limit" | "market">("limit")
  const [price, setPrice] = useState("0")
  const [lotSize, setLotSize] = useState("0.01")
  const [tp, setTp] = useState(false)
  const [sl, setSl] = useState(false)
  const [balance, setBalance] = useState(0)
  const [loading, setLoading] = useState(false)

  const [baseSymbol, quoteSymbol] = pair.split("_")
  const user = auth.currentUser
  const quantity = UNITS_PER_LOT * parseFloat(lotSize)
  const total = (parseFloat(price) * quantity).toFixed(2)

  useEffect(() => {
    if (!user) return
    const fetchBalance = async () => {
      try {
        const ref = doc(db, "users", user.uid, "balance", "main")
        const snap = await getDoc(ref)
        if (snap.exists()) {
          const data = snap.data()
          setBalance(typeof data.amount === "number" ? data.amount : 0)
        }
      } catch {
        toast.error("Failed to load balance")
      }
    }
    fetchBalance()
  }, [user])

  useEffect(() => {
    if (!["limit", "market"].includes(orderType)) return
    const fetchPrice = async () => {
      try {
        const symbol = pair.replace("_", "")
        const res = await fetch(
          `https://api.binance.com/api/v3/ticker/price?symbol=${symbol}`
        )
        const json = await res.json()
        setPrice(parseFloat(json.price).toFixed(2))
      } catch {
        toast.error("Price fetch failed")
      }
    }
    fetchPrice()
  }, [pair, orderType])

  const handleSubmit = async () => {
    if (!user) {
      toast.error("Not authenticated")
      return
    }

    const px = parseFloat(price)
    const ls = parseFloat(lotSize)

    if (isNaN(px) || px <= 0) {
      toast.error("Enter a valid price")
      return
    }

    if (isNaN(ls) || ls <= 0) {
      toast.error("Enter a valid lot size")
      return
    }

    const tradeAmount = px * quantity

    if (side === "buy" && tradeAmount > balance) {
      toast.error("Insufficient balance")
      return
    }

    setLoading(true)

    try {
      const trade: Trade = {
        crypto: pair.replace("_", "/"),
        tradeAction: side === "buy" ? "BUY" : "SELL",
        tradePrice: px,
        priceNow: px,
        tradeAmount,
        profitLose: 0,
        createdAt: serverTimestamp(),
        status: "OPEN",
        side,
        orderType,
        tp,
        sl
      }

      const tradesRef = collection(db, "users", user.uid, "trades")
      const docRef = await addDoc(tradesRef, trade)

      const balanceRef = doc(db, "users", user.uid, "balance", "main")
      await updateDoc(balanceRef, {
        amount: increment(side === "buy" ? -tradeAmount : tradeAmount)
      })

      onNewOrder?.({ id: docRef.id, ...trade })

      setLotSize("0.01")
      toast.success("✅ Trade submitted successfully")
    } catch (e) {
      console.error(e)
      toast.error("Trade submission failed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="spot-trade-form">
      <div className="side-toggle">
        <button
          className={side === "buy" ? "active buy" : "buy"}
          onClick={() => setSide("buy")}
        >
          Buy
        </button>
        <button
          className={side === "sell" ? "active sell" : "sell"}
          onClick={() => setSide("sell")}
        >
          Sell
        </button>
      </div>

      <div className="order-type-tabs">
        {(["limit", "market"] as const).map((type) => (
          <button
            key={type}
            className={orderType === type ? "active" : ""}
            onClick={() => setOrderType(type)}
          >
            {type}
          </button>
        ))}
      </div>

      <div className="available">
        Available: <strong>{balance.toFixed(2)} {quoteSymbol}</strong>
      </div>

      <div className="form-field">
        <label>Price</label>
        <div className="input-group">
          <input
            type="number"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            disabled={orderType === "market"}
          />
          <span>{quoteSymbol}</span>
        </div>
      </div>

      <div className="form-field">
        <label>Lot Size</label>
        <div className="input-group">
          <select
            value={lotSize}
            onChange={(e) => setLotSize(e.target.value)}
          >
            {LOT_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} lot
              </option>
            ))}
          </select>
          <span>LOT</span>
        </div>
      </div>

      <div className="form-field">
        <label>Total</label>
        <div className="input-group">
          <input type="text" readOnly value={total} />
          <span>{quoteSymbol}</span>
        </div>
      </div>

      <div className="checkboxes">
        <label>
          <input
            type="checkbox"
            checked={tp}
            onChange={() => setTp((v) => !v)}
          />{" "}
          TP
        </label>
        <label>
          <input
            type="checkbox"
            checked={sl}
            onChange={() => setSl((v) => !v)}
          />{" "}
          SL
        </label>
      </div>

      <button
        className={`submit ${side}`}
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading
          ? "Processing..."
          : side === "buy"
          ? `Buy ${baseSymbol}`
          : `Sell ${baseSymbol}`}
      </button>

      <div className="fees-row">
        <span className="fee">Maker 0.000% / Taker 0.050%</span>
        <button className="promo">💎 Get 50% Off</button>
      </div>
    </div>
  )
}

export default TradeForm
